type CustomEventType = {
  eventName: string
  eventData: Record<string, any>
}

interface EventQueue {
  events: CustomEventType[]
  isProcessing: boolean
}

const GTM_TIMEOUT_IN_MS = 10000
const POLL_INTERVAL_IN_MS = 100

const eventQueue: EventQueue = {
  events: [],
  isProcessing: false,
}

let gtmLoadPromise: Promise<void> | null = null

function validateEvent({ eventName, eventData }: CustomEventType): void {
  if (!eventName || typeof eventName !== 'string') {
    throw new Error('eventName must be a non-empty string')
  }

  if (!eventData || typeof eventData !== 'object') {
    throw new Error('eventData must be a valid object')
  }
}

function checkGTMReady(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  return !!window?.gtm_ready
}

function createGTMTimeout(): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error(`GTM failed to load after ${GTM_TIMEOUT_IN_MS}ms`))
    }, GTM_TIMEOUT_IN_MS)
  })
}

function pollForGTM(): Promise<void> {
  return new Promise((resolve, reject) => {
    const startTime = Date.now()

    const poll = () => {
      if (checkGTMReady()) {
        resolve()

        return
      }

      if (Date.now() - startTime >= GTM_TIMEOUT_IN_MS) {
        reject(new Error(`GTM timeout after ${GTM_TIMEOUT_IN_MS}ms`))

        return
      }

      setTimeout(poll, POLL_INTERVAL_IN_MS)
    }

    poll()
  })
}

async function waitForGTM(): Promise<void> {
  if (gtmLoadPromise) {
    return gtmLoadPromise
  }

  gtmLoadPromise = Promise.race([pollForGTM(), createGTMTimeout()])

  try {
    await gtmLoadPromise

    flushQueue()
  } catch (error) {
    console.warn('[GTM Queue] Failed to load GTM:', error)

    flushQueue()
  } finally {
    gtmLoadPromise = null
  }
}

function dispatchEvent(event: CustomEventType): void {
  try {
    document.dispatchEvent(
      new CustomEvent(event.eventName, { detail: event.eventData })
    )
  } catch (error) {
    console.error(`Failed to dispatch event ${event.eventName}:`, error)
  }
}

function flushQueue(): void {
  if (eventQueue.isProcessing || eventQueue.events.length === 0) {
    return
  }

  eventQueue.isProcessing = true

  try {
    while (eventQueue.events.length > 0) {
      const event = eventQueue.events.shift()

      if (event) {
        dispatchEvent(event)
      }
    }
  } finally {
    eventQueue.isProcessing = false
  }
}

export function dispatchAmplitudeCustomEvent({
  eventName,
  eventData,
}: CustomEventType): void {
  try {
    validateEvent({ eventName, eventData })
  } catch (error) {
    console.warn('Invalid event data:', error)

    return
  }

  const event: CustomEventType = { eventName, eventData }

  if (checkGTMReady()) {
    dispatchEvent(event)

    return
  }

  eventQueue.events.push(event)

  if (!eventQueue.isProcessing) {
    waitForGTM().catch(() => {
      // Error already logged in waitForGTM
    })
  }
}
